import { Fragment, useEffect, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, TileLayer } from 'react-leaflet';
import { useParams } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';

const API_BASE = '/webapi';

interface SourceProcess {
  type: string;
  label: string;
  title: string;
  date: string | null;
  location: string | null;
  reference: string | null;
}

interface PublicMapData {
  lat: number;
  lng: number;
  mode: 'approx' | 'diffused';
  name: string | null;
  city: string | null;
}

interface SafeContainer {
  id: number;
  kind: string;
  containerCode: string;
  originType: string | null;
  originLabel: string | null;
  productName: string;
  siteName: string | null;
  apiaryName: string | null;
  apiaryCity: string | null;
  extractedAt: string | null;
  collectedAt: string | null;
  waterPercent: number | null;
  nectarLabel: string | null;
  businessName: string;
  sourceProcess: SourceProcess | null;
  map: PublicMapData | null;
}

interface SourceContainer {
  containerId: number;
  containerCode: string;
  productName: string | null;
  qtyKg: number;
  sharePercent: number | null;
  waterPercent: number | null;
  nectarLabel: string | null;
  originLabel: string | null;
  originDate: string | null;
  map?: PublicMapData | null;
}

interface FlavorItem {
  productName: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface PublicInfoResponse {
  container: SafeContainer;
  sourceContainers: SourceContainer[];
  flavorItems: FlavorItem[];
  faq: FaqItem[];
}

function formatDate(value: string | null): string {
  if (!value) {
    return '-';
  }

  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return value.slice(0, 10);
  }

  return date.toLocaleDateString('sv-SE');
}

function formatNumber(value: number, decimals = 1): string {
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
}

function formatPublicContainerId(containerId: number | null | undefined): string {
  return typeof containerId === 'number' && Number.isFinite(containerId) && containerId > 0 ? String(containerId) : '-';
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[150px_1fr] gap-3 border-b border-gray-100 py-2 text-sm last:border-b-0">
      <div className="font-medium text-gray-600">{label}</div>
      <div className="text-gray-900">{value}</div>
    </div>
  );
}

function PublicMap({ map }: { map: PublicMapData }) {
  const center = useMemo<[number, number]>(() => [map.lat, map.lng], [map.lat, map.lng]);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <MapContainer center={center} zoom={8} scrollWheelZoom={false} style={{ height: 260, width: '100%' }}>
        <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <CircleMarker center={center} radius={9} pathOptions={{ color: '#f97316', fillColor: '#f97316', fillOpacity: 0.9 }} />
      </MapContainer>
      <div className="border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
        {map.name ? <span className="font-medium text-gray-800">{map.name}</span> : null}
        {map.city ? `, ${map.city}` : ''}
        {' • '}
        Ungefärlig position
      </div>
    </div>
  );
}

function InlineSourceMap({ map }: { map: PublicMapData }) {
  const center = useMemo<[number, number]>(() => [map.lat, map.lng], [map.lat, map.lng]);

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
      <MapContainer
        center={center}
        zoom={9}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ height: 150, width: '100%' }}
      >
        <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <CircleMarker center={center} radius={7} pathOptions={{ color: '#f97316', fillColor: '#f97316', fillOpacity: 0.9 }} />
      </MapContainer>
      <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-600">
        {map.name ? <span className="font-medium text-gray-800">{map.name}</span> : 'Bigård'}
        {map.city ? `, ${map.city}` : ''}
        {' • '}
        Ungefärlig position
      </div>
    </div>
  );
}

export function ContainerInfoPage() {
  const { containerId } = useParams<{ containerId: string }>();
  const [data, setData] = useState<PublicInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = containerId ? parseInt(containerId, 10) : 0;
    if (id < 1) {
      setError('Ogiltigt hink-ID.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/public/container-info?id=${id}`, { credentials: 'omit' })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(res.status === 404 ? 'Hinken hittades inte.' : 'Kunde inte hämta batchinformationen.');
        }
        return res.json() as Promise<PublicInfoResponse>;
      })
      .then((payload) => {
        if (!cancelled) {
          setData(payload);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Ett fel uppstod.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [containerId]);

  const container = data?.container ?? null;
  const sourceContainers = data?.sourceContainers ?? [];
  const flavorItems = data?.flavorItems ?? [];
  const faq = data?.faq ?? [];
  const showPrimaryMapInline = Boolean(container?.map) && sourceContainers.length === 0;
  const showPrimaryMapAside = Boolean(container?.map) && sourceContainers.length > 0;

  return (
    <div className="min-h-screen bg-[#fffaf5] text-gray-900">
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-8 sm:px-6">
        <header className="mb-8 text-center">
          <img
            src="/logga.png"
            alt="ApisAI"
            className="mx-auto mb-4 h-12 w-auto"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
          <h1 className="mt-2 text-3xl font-semibold text-gray-900">Var kommer min honungsburk ifrån?</h1>
          <p className="mt-3 text-sm text-gray-600">Spårbar batchinformation från biodling och produktion.</p>
        </header>

        {loading ? (
          <main className="rounded-3xl border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">
            <p className="text-sm text-gray-500">Laddar batchinformation...</p>
          </main>
        ) : null}

        {!loading && error ? (
          <main className="rounded-3xl border border-red-200 bg-white px-6 py-10 text-center shadow-sm">
            <p className="text-base font-medium text-red-700">{error}</p>
          </main>
        ) : null}

        {!loading && !error && container ? (
          <main className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <aside className="order-1 space-y-6 lg:order-2">
              {showPrimaryMapAside && container.map ? <PublicMap map={container.map} /> : null}

              {container.kind === 'HONEY' && faq.length > 0 ? (
                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900">Naturliga egenskaper i honung</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Här förklarar vi sådant som ibland kan se ut som fel, men som oftast är helt naturligt i riktig honung.
                  </p>
                  <div className="mt-4 space-y-4">
                    {faq.map((item) => (
                      <div key={item.question} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{item.question}</p>
                        <p className="mt-1 text-sm leading-6 text-gray-600">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </aside>

            <section className="order-2 space-y-6 lg:order-1">
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-orange-600">Batchinformation</p>
                    {container.sourceProcess ? (
                      <p className="mt-2 text-sm text-gray-600">{container.sourceProcess.title}</p>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-1">
                  <InfoRow label="Producent" value={container.businessName || '-'} />
                  <InfoRow label="Process" value={container.sourceProcess?.label ?? '-'} />
                  <InfoRow label="Datum" value={formatDate(container.sourceProcess?.date ?? container.extractedAt ?? container.collectedAt)} />
                  {container.nectarLabel ? <InfoRow label="Nektar" value={container.nectarLabel} /> : null}
                  {container.waterPercent !== null ? <InfoRow label="Vattenhalt" value={`${formatNumber(container.waterPercent, 1)}%`} /> : null}
                </div>

                {showPrimaryMapInline && container.map ? (
                  <div className="mt-6">
                    <PublicMap map={container.map} />
                  </div>
                ) : null}
              </div>

              {sourceContainers.length > 0 ? (
                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Ingående hinkar</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {container.sourceProcess?.type === 'FLAVOR'
                        ? 'Honungen i denna smaksättning kommer från följande källhinkar.'
                        : 'Denna blandning består av följande källhinkar.'}
                    </p>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-orange-50 text-left text-gray-700">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Hink</th>
                          <th className="px-4 py-3 font-semibold">Produkt</th>
                          <th className="px-4 py-3 font-semibold text-right">Kg</th>
                          <th className="px-4 py-3 font-semibold text-right">Andel</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {sourceContainers.map((source) => (
                          <Fragment key={`${source.containerId}-${source.qtyKg}`}>
                            <tr>
                              <td className="px-4 py-3 align-top font-medium text-gray-900">
                                {formatPublicContainerId(source.containerId)}
                                <div className="mt-1 text-xs font-normal text-gray-500">
                                  {source.originLabel ?? '-'}
                                  {source.originDate ? ` • ${formatDate(source.originDate)}` : ''}
                                </div>
                              </td>
                              <td className="px-4 py-3 align-top text-gray-700">
                                <div>{source.productName ?? '-'}</div>
                                {source.nectarLabel || source.waterPercent !== null ? (
                                  <div className="mt-1 text-xs text-gray-500">
                                    {source.nectarLabel ?? ''}
                                    {source.nectarLabel && source.waterPercent !== null ? ' • ' : ''}
                                    {source.waterPercent !== null ? `${formatNumber(source.waterPercent, 1)}% vatten` : ''}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-4 py-3 text-right align-top text-gray-700">{formatNumber(source.qtyKg, 3)}</td>
                              <td className="px-4 py-3 text-right align-top text-gray-700">
                                {source.sharePercent !== null ? `${formatNumber(source.sharePercent, 1)}%` : '-'}
                              </td>
                            </tr>
                            {source.map ? (
                              <tr>
                                <td colSpan={4} className="px-4 pb-4 pt-0">
                                  <InlineSourceMap map={source.map} />
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {flavorItems.length > 0 ? (
                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900">Smaksättning</h3>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {flavorItems.map((item) => (
                      <div key={item.productName} className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{item.productName}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          </main>
        ) : null}

        <footer className="mt-10 border-t border-orange-100 pt-5 text-center text-sm text-gray-500">
          Om ApisAI:{' '}
          <a href="https://apisai.se" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
            apisai.se
          </a>
        </footer>
      </div>
    </div>
  );
}
